import React from 'react';
import { Box, HStack, Icon, useRadio, useRadioGroup } from '@chakra-ui/react';
import { LuCheck } from 'react-icons/lu';
import { DOC_TYPES } from './sdkDocsModel';

const TypeChip = (props) => {
    const { state, getInputProps, getRadioProps } = useRadio(props);
    const isChecked = state.isChecked;

    return (
        <Box as="label" cursor={props.isDisabled ? 'not-allowed' : 'pointer'}>
            {/* Chakra's hidden radio input is `position: absolute`, so it still
                sits inside this chip group's normal scroll flow. Clicking it
                (useRadio focuses it programmatically to sync state) makes the
                browser run its default scroll-into-view for the newly focused
                element — and because this chip group lives inside a Modal with
                scrollBehavior="inside", that scroll doesn't just move the
                Modal's own scrollable body, it can shift the fixed-positioned
                Modal itself. Pinning the (already invisible, 1px) input to
                `position: fixed` takes it out of any scrollable ancestor's flow
                entirely, so there's nothing for the browser to scroll toward —
                the visible chip's checked/focus styling (driven by getRadioProps
                below) is unaffected either way. */}
            <input {...getInputProps()} style={{ position: 'fixed', top: 0, left: 0 }} />
            <HStack
                {...getRadioProps()}
                spacing={1.5}
                px={3}
                h="30px"
                borderRadius="full"
                border="1px solid"
                fontSize="12px"
                fontWeight="600"
                letterSpacing="0.02em"
                userSelect="none"
                transition="all 0.15s ease"
                borderColor={isChecked ? 'brand.500' : 'gray.200'}
                bg={isChecked ? 'brand.500' : 'white'}
                color={isChecked ? 'white' : 'gray.600'}
                opacity={props.isDisabled ? 0.5 : 1}
                _hover={props.isDisabled || isChecked ? {} : { borderColor: 'brand.500', color: 'brand.500' }}
                _focusVisible={{ boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)' }}
            >
                {isChecked && <Icon as={LuCheck} boxSize="13px" />}
                <Box as="span">{props.label}</Box>
            </HStack>
        </Box>
    );
};

/**
 * Single-select chips for the documentation type (PUSH / EVENT / STORIES).
 * Exactly one is always selected and its id (1, 2 or 3) is what gets saved.
 */
const TypeChipGroup = ({ value, onChange, isDisabled }) => {
    const { getRootProps, getRadioProps } = useRadioGroup({
        // Radio inputs carry string values; map back to the numeric type id.
        value: String(value),
        onChange: (next) => onChange(Number(next)),
    });

    return (
        <HStack {...getRootProps()} spacing={2}>
            {DOC_TYPES.map(type => (
                <TypeChip
                    key={type.id}
                    label={type.label}
                    isDisabled={isDisabled}
                    {...getRadioProps({ value: String(type.id) })}
                />
            ))}
        </HStack>
    );
};

export default TypeChipGroup;
